"use client";
import { useCallback, useRef, useImperativeHandle, forwardRef } from "react";
import {
  InfiniteScrollListElement,
  VirtualListProps,
  VirtualListRef,
} from "./types";

/**
 * 虚拟列表封装，底层为 @wc-lib/infinite-scroll-list Web Component。
 * - 默认：触底加载（end-reached）
 * - mode="refresh"：下拉刷新
 * - mode="load-prev"：触顶加载上一页（start-reached + preserveScrollOnPrepend）
 */
const VirtualList = forwardRef<VirtualListRef, VirtualListProps>((props, ref) => {
  const listRef = useRef<InfiniteScrollListElement | null>(null);
  const {
    renderLoading,
    renderNoMore,
    renderRefresh,
    renderPrevNoMore,
    mode,
    hasNextPage = true,
    hasPrevPage = false,
    onEndReached,
    onEndReachedThreshold = "100",
    onStartReached,
    onStartReachedThreshold = "0",
    children,
    loadingContainerClassName,
    noMoreContainerClassName,
    className,
    refreshContainerClassName,
    prevNoMoreContainerClassName,
    refreshThreshold = "0",
    onRefresh,
    onRefreshPulling,
    isRefreshing,
    refreshFeedbackThreshold = "40",
    prependScrollPreserve = true,
    ...rest
  } = props;

  const isRefreshMode = mode === "refresh";
  const isLoadPrevMode = mode === "load-prev";

  useImperativeHandle(
    ref,
    () => ({
      get target() {
        return listRef.current;
      },
      scrollToTop: () => {
        listRef.current?.scrollToTop();
      },
      scrollToTopAndRefresh: () => {
        listRef.current?.scrollToTopAndRefresh();
      },
      preserveScrollOnPrepend: async (fn) => {
        if (listRef.current) {
          await listRef.current.preserveScrollOnPrepend(fn);
          return;
        }
        await fn();
      },
    }),
    [],
  );

  const setListRef = useCallback(
    (node: InfiniteScrollListElement | null) => {
      const prev = listRef.current;
      if (prev) {
        prev.removeEventListener("end-reached", onEndReached as EventListener);
        prev.removeEventListener("refresh", onRefresh as EventListener);
        prev.removeEventListener(
          "refresh-pulling",
          onRefreshPulling as EventListener,
        );
        prev.removeEventListener(
          "start-reached",
          onStartReached as EventListener,
        );
      }

      if (node) {
        listRef.current = node;
        node.addEventListener("end-reached", onEndReached as EventListener);
        node.addEventListener("refresh", onRefresh as EventListener);
        node.addEventListener(
          "refresh-pulling",
          onRefreshPulling as EventListener,
        );
        node.addEventListener(
          "start-reached",
          onStartReached as EventListener,
        );
      } else {
        listRef.current = null;
      }
    },
    [onEndReached, onRefresh, onRefreshPulling, onStartReached],
  );

  return (
    // @ts-expect-error - Web Components 类型定义（见 src/types.d.ts）
    <infinite-scroll-list
      {...rest}
      className={className}
      ref={setListRef}
      {...(mode ? { mode } : {})}
      has-next-page={hasNextPage ? "true" : "false"}
      on-end-reached-threshold={onEndReachedThreshold}
      {...(isLoadPrevMode
        ? {
            "has-prev-page": hasPrevPage ? "true" : "false",
            "on-start-reached-threshold": onStartReachedThreshold,
            "prepend-scroll-preserve": prependScrollPreserve
              ? "true"
              : "false",
          }
        : {})}
      {...(isRefreshMode
        ? {
            "refresh-feedback-threshold": refreshFeedbackThreshold,
            "refresh-threshold": refreshThreshold,
            "is-refreshing": isRefreshing ? "true" : "false",
          }
        : {})}
    >
      {(isRefreshMode || isLoadPrevMode) && (
        <div slot="refresh" className={refreshContainerClassName}>
          {renderRefresh}
        </div>
      )}
      {isLoadPrevMode && (
        <div slot="prev-no-data" className={prevNoMoreContainerClassName}>
          {renderPrevNoMore}
        </div>
      )}
      {children}
      <div slot="loading" className={loadingContainerClassName}>
        {renderLoading}
      </div>
      <div slot="no-data" className={noMoreContainerClassName}>
        {renderNoMore}
      </div>
    {/* @ts-expect-error - Web Components 类型定义 */}
    </infinite-scroll-list>
  );
});

VirtualList.displayName = "VirtualList";

export { VirtualList };
